// src/components/presence/PresencePanel.jsx
// PresencePanel — the single render path for a Presence at its thin level.
//
// A Presence is one entity (a directory listing now; a community or an advert later) that
// renders through THIS panel at increasing richness. The backing source is a SWITCH, not a
// fork: PresencePanel takes an already-normalised source object (see presenceSource.js) and
// never branches on where it came from. Membership / sub-groups / federation / part-of are
// OPTIONAL SLOTS — the listing source does not supply them, so they render nothing; a future
// community source passes them as nodes and the same panel re-enables those sections without
// a fork. This is the load-bearing constraint of the Presence roadmap: one panel, one path.
//
// Density is a property of the SURFACE, not the entity:
//   - density="compact"     — the directory accordion. Dense, text-first, phone-book tight.
//   - density="comfortable" — a future group-backed modal (a presentation element). Roomier.
// Structure and logic are lifted from GroupHomePanel's identity block; its spacious spacing
// is NOT — compact takes the directory accordion's tightness as its spacing reference.

import React from 'react';
import { useTheme } from '../../themes/ThemeProvider';
import { useTranslation } from '../../i18n/useTranslation';
import InitialPlaceholder from '../core/InitialPlaceholder';
import { formatWebsiteDisplay } from '../../utils/formatText';
import { CONTACT_GLYPH } from '../../utils/contactFieldIcons';

const DENSITY = {
  compact: {
    thumb: 38, thumbRadius: 8, thumbFont: 16,
    headerDir: 'row', headerAlign: 'center', headerGap: 8, headerTextAlign: 'left',
    nameFont: 13, tagFont: 10, tagPad: '1px 6px',
    descFont: 12, contactFont: 13, addressFont: 12, linkFont: 11,
    // Tight rhythm matching the DirectoryNav category rows — minimal inter-line/section margin.
    blockGap: 3, lineGap: '2px', rootPad: '2px 0'
  },
  comfortable: {
    thumb: 96, thumbRadius: 12, thumbFont: 34,
    headerDir: 'column', headerAlign: 'center', headerGap: 10, headerTextAlign: 'center',
    nameFont: 17, tagFont: 11, tagPad: '2px 8px',
    descFont: 13, contactFont: 14, addressFont: 12, linkFont: 12,
    blockGap: 12, lineGap: '4px', rootPad: '4px 0'
  }
};

const PresencePanel = ({
  source,
  density = 'compact',
  onSuggestCorrection,
  onClose,
  // Optional slots — nodes the listing source omits; a future community source supplies them.
  membership = null,
  subGroups = null,
  federation = null,
  partOf = null
}) => {
  const theme = useTheme();
  const { t } = useTranslation('frontpage');
  const d = DENSITY[density] || DENSITY.compact;

  if (!source) return null;
  const { image, name, description, contacts = [], tags = [], backing } = source;

  // Compact (directory) is text-first YellowPages — no image placeholder, no panel name (the
  // caller's accordion row already shows it above the open panel, so a panel name line would
  // double-print). Comfortable (the future modal, no row above it) keeps its full
  // name/placeholder chrome. showName is overridable for a future compact caller that has no
  // row header of its own.
  //
  // Contact-line glyphs (CONTACT_GLYPH, in renderContact below) used to be comfortable-only
  // on the same "text-first" reasoning — reversed 16 Aug, not a bug fix: once website started
  // rendering as a bare hostname, phone/email/website/address lines read as near-identical
  // strings stacked together with nothing marking which was which. Glyphs now always show,
  // both densities; the ambiguity they solve isn't density-specific.
  const showThumb = Boolean(image) || density === 'comfortable';
  const showName = density === 'comfortable';

  const renderContact = (c, i) => {
    if (c.type === 'address') {
      return (
        <div key={`addr-${i}`} style={{ fontSize: d.addressFont, color: '#6b7280', marginTop: d.lineGap }}>
          {CONTACT_GLYPH.address}{' '}{c.value}
        </div>
      );
    }
    let href = c.value;
    let display = c.value;
    if (c.type === 'phone') href = `tel:${c.value}`;
    else if (c.type === 'email') href = `mailto:${c.value}`;
    else if (c.type === 'website') {
      href = c.value.startsWith('http') ? c.value : `https://${c.value}`;
      display = formatWebsiteDisplay(c.value);
    }
    return (
      <div key={`${c.type}-${i}`} style={{ fontSize: d.contactFont, color: '#374151', marginTop: d.lineGap }}>
        {CONTACT_GLYPH[c.type]}{' '}
        <a
          href={href}
          {...(c.type === 'website' ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
          style={{ color: theme.primary, textDecoration: 'none' }}
        >
          {display}
        </a>
      </div>
    );
  };

  const linkButtonStyle = {
    display: 'block', marginTop: d.blockGap, background: 'none', border: 'none',
    padding: 0, cursor: 'pointer', fontFamily: 'OpenSauceSans, sans-serif',
    fontSize: d.linkFont, fontWeight: '600', color: theme.primary, textAlign: 'left'
  };

  // Compact used to carry its own hairline left rule + indent, offsetting the opened body from
  // the flush row above it. Removed (hairline-and-indent correction, 16 Aug): every compact
  // mount is now wrapped in the caller's open-entry border (DirectorySection.jsx's
  // openEntryStyle) — a second framing device on the same content was redundant, and the
  // indent cost width a narrow column can't spare. The border is now the sole open-state
  // indicator.
  const rootStyle = density === 'compact'
    ? { fontFamily: 'OpenSauceSans, sans-serif', padding: '0 0 4px 0' }
    : { fontFamily: 'OpenSauceSans, sans-serif', padding: d.rootPad };

  return (
    <div style={rootStyle}>
      {/* Header — thumbnail and/or name, only where the surface warrants each. Compact with no
          image and no panel name renders nothing here (the caller's row is the header).
          Compact-with-image (future) shows a small inline thumbnail; comfortable shows both. */}
      {(showThumb || showName) && (
        <div style={{
          display: 'flex', flexDirection: d.headerDir, alignItems: d.headerAlign,
          gap: d.headerGap, textAlign: d.headerTextAlign
        }}>
          {showThumb && (
            <InitialPlaceholder
              name={name}
              image={image}
              fontSize={d.thumbFont}
              style={{ width: d.thumb, height: d.thumb, borderRadius: d.thumbRadius }}
            />
          )}
          {showName && (
            <div style={{ fontWeight: '700', fontSize: d.nameFont, color: '#1f2937', minWidth: 0 }}>
              {name}
            </div>
          )}
        </div>
      )}

      {/* Tags */}
      {tags.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: d.blockGap }}>
          {tags.map(tag => (
            <span key={tag} style={{
              display: 'inline-block', padding: d.tagPad, background: '#e8edf3',
              borderRadius: '3px', fontSize: d.tagFont, color: '#6b7280', fontWeight: '500'
            }}>
              {tag}
            </span>
          ))}
        </div>
      )}

      {/* Description */}
      {description && (
        <div style={{
          fontSize: d.descFont, color: '#6b7280', marginTop: d.blockGap, lineHeight: '1.4'
        }}>
          {description}
        </div>
      )}

      {/* Contacts (phone · email · website · address) */}
      {contacts.length > 0 && (
        <div style={{ marginTop: d.blockGap }}>
          {contacts.map(renderContact)}
        </div>
      )}

      {/* Optional slots — absent for the listing source, supplied by a future community source */}
      {membership}
      {subGroups}
      {federation}
      {partOf}

      {/* View group — only when the source resolves to a verified community */}
      {backing?.groupId && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            window.dispatchEvent(new CustomEvent('ournet:navigate', {
              detail: { view: 'group', groupId: backing.groupId }
            }));
            if (onClose) onClose();
          }}
          style={linkButtonStyle}
        >
          {t('view_group')} {'→'}
        </button>
      )}

      {/* Suggest correction — provided (already bound) by the directory surface */}
      {onSuggestCorrection && (
        <button
          onClick={(e) => { e.stopPropagation(); onSuggestCorrection(); }}
          style={linkButtonStyle}
        >
          {t('suggest_correction')} {'→'}
        </button>
      )}
    </div>
  );
};

export default PresencePanel;
